import React, { useState, useEffect } from 'react';

interface YouTubeGameEndAdProps {
  isVisible: boolean;
  onClose: () => void;
  gameWon: boolean;
}

const YouTubeGameEndAd: React.FC<YouTubeGameEndAdProps> = ({ 
  isVisible, 
  onClose, 
  gameWon 
}) => {
  const [showAd, setShowAd] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Check if user has already clicked the YouTube button
      const hasClickedYouTube = localStorage.getItem('youtube-game-ad-clicked');
      if (hasClickedYouTube === 'true') {
        // User already clicked, skip the ad
        onClose();
        return;
      }
      
      // Show ad after a brief delay
      const timer = setTimeout(() => setShowAd(true), 1000);
      return () => clearTimeout(timer);
    } else {
      setShowAd(false);
    }
  }, [isVisible, onClose]);

  const handleClose = () => {
    setShowAd(false);
    setTimeout(() => onClose(), 300);
  };

  const handleYouTubeClick = () => {
    // Mark that user clicked the YouTube button - never show again
    localStorage.setItem('youtube-game-ad-clicked', 'true');
    console.log('YouTube game ad: User clicked YouTube button - marked as permanent dismissal');
    
    // Close the ad
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center transition-opacity duration-300 ${
        showAd ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ zIndex: 10000 }}
    >
      <div 
        className={`bg-gradient-to-br from-red-500 via-red-600 to-red-700 text-white rounded-2xl shadow-2xl p-6 mx-4 max-w-md w-full relative overflow-hidden transform transition-all duration-300 ${
          showAd ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Animated background elements */}
        <div className="absolute -top-4 -right-4 w-24 h-24 bg-red-400 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white rounded-full opacity-10 animate-bounce"></div>
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-white hover:text-red-200 transition-colors z-10"
          aria-label="Sluiten"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header with celebration */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">
            {gameWon ? '🎉' : '💪'}
          </div>
          <h2 className="text-xl font-bold mb-2">
            {gameWon ? 'Gefeliciteerd!' : 'Goed geprobeerd!'}
          </h2>
          <p className="text-red-100 text-sm">
            {gameWon 
              ? 'Je hebt het woord geraden!' 
              : 'Beter geluk volgende keer!'
            }
          </p>
        </div>

        {/* Main content */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
          </div>
          
          <h3 className="text-lg font-bold mb-2">Steun dit spel! 🎵</h3>
          <p className="text-red-100 text-sm mb-4 leading-relaxed">
            Vond je Woordol leuk? Ontdek meer puzzels, muziek en entertainment op mijn YouTube kanaal. 
            Abonneer je en luister naar enkele geweldige nummers!
          </p>
          
          {/* Features */}
          <div className="flex justify-center space-x-4 mb-4 text-xs">
            <div className="text-center">
              <div className="text-lg">🧩</div>
              <div>Puzzels</div>
            </div>
            <div className="text-center">
              <div className="text-lg">🎵</div>
              <div>Muziek</div>
            </div>
            <div className="text-center">
              <div className="text-lg">🎮</div>
              <div>Games</div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="space-y-3">
          <a
            href="https://www.youtube.com/@slimmii"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-white text-red-600 py-3 px-4 rounded-xl font-bold text-center hover:bg-red-50 transition-all duration-200 transform hover:scale-105 shadow-lg"
            onClick={handleYouTubeClick}
          >
            <div className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span>Bezoek YouTube Kanaal</span>
            </div>
          </a>
          
          <button
            onClick={handleClose}
            className="block w-full bg-white bg-opacity-20 text-white py-2 px-4 rounded-xl font-medium text-center hover:bg-opacity-30 transition-all duration-200"
          >
            Misschien later
          </button>
        </div>

        {/* Small disclaimer */}
        <p className="text-center text-red-200 text-xs mt-4 opacity-80">
          Gratis content • Geen spam • Geweldige muziek
        </p>
      </div>
    </div>
  );
};

export default YouTubeGameEndAd;
